import { ELUVE_FHIR_NAMESPACE } from '@eluve/fhir-utils';

// Plan Shape vocabulary lives under the shared Eluve FHIR namespace authority
// (`@eluve/fhir-utils`); re-exported here so existing consumers keep working.
export { ELUVE_FHIR_NAMESPACE };

export const PLAN_SHAPE_EXTENSION_URL = `${ELUVE_FHIR_NAMESPACE}/StructureDefinition/plan-shape`;
export const PLAN_SHAPE_CODE_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/CodeSystem/plan-shape`;

// Phase codings on authored phased plans.
export const ELUVE_PHASE_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/CodeSystem/care-plan-phase`;

// Legacy phase-coding systems carried by data seeded from the orchard/myodetox
// dataset (the local/demo Medplum project is seeded with myodetox-namespaced
// PlanDefinitions). The phased decoders accept these as fallbacks so the
// matcher + renderers recognize the seeded rehab library, not just plans
// authored in eluve's Plan Builder. Once the seed/bridge normalizes everything
// to `https://eluve.com` (see the seed-normalization ticket), these become
// dead-but-harmless and can be dropped.
export const ATLAS_PHASE_SYSTEM =
  'https://atlas.eluve.com/CodeSystem/care-plan-phase';
export const MYODETOX_PHASE_SYSTEM =
  'http://myodetox.com/CodeSystem/care-plan-phase';

// Every phase-coding system a phased plan may carry, newest first.
export const PHASE_SYSTEMS = [
  ELUVE_PHASE_SYSTEM,
  ATLAS_PHASE_SYSTEM,
  MYODETOX_PHASE_SYSTEM,
] as const;

export function isPhaseSystem(system: string | undefined): boolean {
  return (
    system !== undefined &&
    (PHASE_SYSTEMS as readonly string[]).includes(system)
  );
}

// Codes that mark which structural slot of a shape an action/goal fills
// (baseline vs event for episodic; control-band vs goal, cadence, review-rule
// for continuous). Purely structural — gives the decode a deterministic handle.
export const ELUVE_PLAN_SECTION_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/CodeSystem/plan-section`;

// Canonical URL base for authored plans: `${base}/<slug>` minted at save.
export const PLAN_CANONICAL_BASE = `${ELUVE_FHIR_NAMESPACE}/PlanDefinition`;

// Canonical URL base for authored ActivityDefinitions. v1 ADs are PLAN-SCOPED:
// `${base}/<plan-slug>/<activity-slug>`, so two plans that both prescribe chin
// tucks each own one.
export const ACTIVITY_CANONICAL_BASE = `${ELUVE_FHIR_NAMESPACE}/ActivityDefinition`;

// Machine-readable exercise dosing: sets×reps×hold has no first-class FHIR
// home, so one complex extension carries it — sub-extensions
// `sets`/`reps`/`holdSeconds` (valueInteger) and `resistance` (valueString).
// Written on the AD (default) and on each referencing action (occurrence).
export const EXERCISE_DOSAGE_EXTENSION_URL = `${ELUVE_FHIR_NAMESPACE}/StructureDefinition/exercise-dosage`;

// Phase association for patient-bound resources (the instance layer, ADR-0013).
// Seeded instances carry the phase on CarePlan.activity.detail.code; tailored
// instances also stamp it on each ServiceRequest (valueCoding in a phase system)
// so instance activities group by phase without a PlanDefinition in hand.
export const ACTIVITY_PHASE_EXTENSION_URL = `${ELUVE_FHIR_NAMESPACE}/StructureDefinition/careplan-activity-phase`;

// Complex extension on a draft CarePlan carrying the AI matcher's verdict
// (ENG-4699): the recommendation card reads it. Sub-extensions `confidence`
// (valueDecimal) and repeated `rationale` (each a nested extension with
// `label`/`text` valueString sub-extensions).
export const CARE_PLAN_MATCH_EXTENSION_URL = `${ELUVE_FHIR_NAMESPACE}/StructureDefinition/care-plan-match`;

// Identifier system for the FHIR Task that tracks the async care-plan-generation
// job (ENG-4706). Keyed by appointment id — the same value as the draft CarePlan
// — so the frontend can poll the in-flight Task before the draft exists. Mirrors
// the backend's `buildIdentifierSystem('care-plan-generation-job')` (the frontend
// can't import the nestjs-medplum helper).
export const CARE_PLAN_GENERATION_JOB_IDENTIFIER_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/care-plan-generation-job-id`;

// `Task.statusReason.coding.system` for the generation job's terminal outcome
// (ENG-4706). A completed Task with no `focus` carries `no-match` (the matcher
// quiet-skipped — no plan was recommended); a `failed` Task carries `error`
// (the job threw). Lets the UI render "no plan recommended" distinctly from
// "generation failed".
export const CARE_PLAN_GENERATION_OUTCOME_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/care-plan-generation-outcome`;

export const CarePlanGenerationOutcome = {
  NO_MATCH: 'no-match',
  ERROR: 'error',
} as const;

export type CarePlanGenerationOutcomeCode =
  (typeof CarePlanGenerationOutcome)[keyof typeof CarePlanGenerationOutcome];

export const UCUM_SYSTEM = 'http://unitsofmeasure.org';

// Discriminator system for phase spine-stick ServiceRequests (ADR-0016): a
// `category` coding under this system (code `careplan-phase`) marks a stick,
// distinct from leaf SRs' `careplan-activity-phase` extension. Decoders accept
// the orchard-era atlas system on read.
export const CAREPLAN_SEGMENT_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/CodeSystem/careplan-segment`;
export const ATLAS_CAREPLAN_SEGMENT_SYSTEM =
  'https://atlas.eluve.com/CodeSystem/careplan-segment';
export const CAREPLAN_SEGMENT_SYSTEMS = [
  CAREPLAN_SEGMENT_SYSTEM,
  ATLAS_CAREPLAN_SEGMENT_SYSTEM,
] as const;
export const CAREPLAN_PHASE_SEGMENT_CODE = 'careplan-phase';

// Care Library membership tag (lean orchard port). The eluve seeds already
// mint this system on the 15 library ADs; orchard's atlas resource-tag tokens
// (`care-library` / legacy `exercise-library`) are accepted on read.
export const CATALOG_MEMBERSHIP_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/CodeSystem/catalog-membership`;
export const CARE_LIBRARY_TAG_CODE = 'care-library';
export const CARE_LIBRARY_TAG_TOKEN = `${CATALOG_MEMBERSHIP_SYSTEM}|${CARE_LIBRARY_TAG_CODE}`;
export const ATLAS_RESOURCE_TAG_SYSTEM =
  'https://atlas.eluve.com/CodeSystem/resource-tag';
export const ATLAS_EXERCISE_LIBRARY_TAG_CODE = 'exercise-library';

export const CATEGORY_AXIS_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/CodeSystem/category-axis`;
export const OFFERING_TYPE_AXIS_CODE = 'offering-type';
export const OFFERING_TYPE_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/CodeSystem/offering-type`;

// Where a CarePlan.activity carries its phase spine stick's reference. R4's
// cpl-3 invariant forbids activity.reference alongside activity.detail (and
// the worktree Medplum $validate enforces it — probed 2026-07-12), so the
// stick ref rides this extension instead of the orchard-style reference zip.
// Readers accept activity.reference too for orchard-written legacy plans.
export const ACTIVITY_STICK_EXTENSION_URL = `${ELUVE_FHIR_NAMESPACE}/StructureDefinition/careplan-activity-stick`;

// "Phase 2 — Align" → "phase-2-align". Used for phase codes and the canonical
// URL slug. ASCII-folding is deliberately not attempted; non-ASCII drops out.
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// "Lumbar restoration — 5 phase" → "LumbarRestoration5Phase". FHIR `name` is a
// machine-readable identifier (computer-friendly, per the spec's invariant).
export function pascalName(title: string): string {
  return title
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
}
